/**
 * Navigation Registry — dynamic self-registration pattern.
 *
 * Feature modules call `registerPage()` / `registerAction()` at import time.
 * The registry is the single source of truth for Command Palette, AI Chat Bar,
 * and Activity Tracker. Seed pages below bootstrap the registry; feature modules
 * can add more without touching this file.
 */

import type { NavItem, QuickAction } from '../types';

// ─── Mutable Registries ──────────────────────────────────────────────────────

const pages: NavItem[] = [];
const actions: QuickAction[] = [];
const registeredPaths = new Set<string>();
const registeredActionIds = new Set<string>();

// ─── Public API ──────────────────────────────────────────────────────────────

/** Register a navigable page. Duplicates (same path) are silently ignored. */
export function registerPage(item: NavItem): void {
    if (registeredPaths.has(item.path)) return;
    registeredPaths.add(item.path);
    pages.push(item);
}

/** Register multiple pages at once. */
export function registerPages(items: NavItem[]): void {
    items.forEach(registerPage);
}

/** Register a quick action. Duplicates (same id) are silently ignored. */
export function registerAction(item: QuickAction): void {
    if (registeredActionIds.has(item.id)) return;
    registeredActionIds.add(item.id);
    actions.push(item);
}

/** Register multiple actions at once. */
export function registerActions(items: QuickAction[]): void {
    items.forEach(registerAction);
}

/** Read-only snapshots for consumers. */
export function getPages(): readonly NavItem[] { return pages; }
export function getActions(): readonly QuickAction[] { return actions; }

// ─── Seed Pages (core platform pages) ────────────────────────────────────────
// Feature modules should call registerPage()/registerPages() in their own code.

registerPages([
    // Dashboard
    { path: '/dashboard', label: 'Dashboard', description: 'Main dashboard overview', category: 'dashboard', icon: '🏠', keywords: ['home', 'overview', 'main', 'welcome'] },

    // Customers
    { path: '/customers', label: 'Customers', description: 'Customer management and list', category: 'customers', icon: '👤', keywords: ['customer', 'client', 'user', 'list', 'people'], requiredPermission: 'customers:view' },

    // Institutions
    { path: '/institutions', label: 'Institutions', description: 'Manage banking institutions', category: 'institutions', icon: '🏦', keywords: ['bank', 'institution', 'org', 'organisation'], requiredPermission: 'platform:institutions:manage' },
    { path: '/institutions/new', label: 'New Institution', description: 'Create a new banking institution', category: 'institutions', icon: '➕', keywords: ['create', 'new', 'add', 'institution', 'bank'], requiredPermission: 'platform:institutions:create' },

    // Users
    { path: '/users', label: 'Users', description: 'Admin user management', category: 'users', icon: '👥', keywords: ['admin', 'user', 'staff', 'team', 'people', 'employees'], requiredPermission: 'users:view' },

    // Settings & Profile
    { path: '/settings', label: 'Settings', description: 'Platform settings', category: 'settings', icon: '⚙️', keywords: ['settings', 'configure', 'preference'], requiredPermission: 'settings:view' },
    { path: '/profile', label: 'Profile', description: 'Your admin profile', category: 'settings', icon: '👤', keywords: ['profile', 'account', 'me', 'my'] },
]);

// ─── Seed Actions ────────────────────────────────────────────────────────────

registerActions([
    { id: 'toggle-theme', label: 'Toggle Theme', description: 'Switch between light and dark mode', icon: '🌓', action: 'toggleTheme', keywords: ['theme', 'dark', 'light', 'mode', 'toggle'] },
]);

// ─── Legacy aliases (consumers that import PAGE_REGISTRY directly) ───────────

/** @deprecated Use getPages() instead. */
export const PAGE_REGISTRY = pages;
/** @deprecated Use getActions() instead. */
export const ACTION_REGISTRY = actions;

// ─── Fuzzy Search ────────────────────────────────────────────────────────────

/**
 * Simple fuzzy search scorer. Returns 0 (no match) to 100 (perfect match).
 */
export function fuzzyScore(query: string, target: string): number {
    const q = query.toLowerCase();
    const t = target.toLowerCase();

    // Exact match
    if (t === q) return 100;

    // Starts with
    if (t.startsWith(q)) return 90;

    // Contains
    if (t.includes(q)) return 70;

    // Fuzzy character match
    let qi = 0;
    let consecutiveBonus = 0;
    let lastMatchIdx = -2;
    for (let ti = 0; ti < t.length && qi < q.length; ti++) {
        if (t[ti] === q[qi]) {
            consecutiveBonus += (ti === lastMatchIdx + 1) ? 5 : 0;
            lastMatchIdx = ti;
            qi++;
        }
    }

    if (qi < q.length) return 0;

    const matchRatio = q.length / t.length;
    return Math.min(60, Math.round(matchRatio * 50) + consecutiveBonus);
}

/**
 * Search the page + action registries with fuzzy matching.
 */
export function searchRegistry(
    query: string,
    options?: { maxResults?: number }
): Array<{ item: NavItem | QuickAction; score: number; type: 'page' | 'action' }> {
    const q = query.toLowerCase().trim();
    if (!q) return [];

    const max = options?.maxResults ?? 15;
    const results: Array<{ item: NavItem | QuickAction; score: number; type: 'page' | 'action' }> = [];

    for (const page of getPages()) {
        const labelScore = fuzzyScore(q, page.label);
        const descScore = fuzzyScore(q, page.description) * 0.7;
        const keywordScore = Math.max(0, ...page.keywords.map(k => fuzzyScore(q, k))) * 0.8;
        const best = Math.max(labelScore, descScore, keywordScore);
        if (best > 0) {
            results.push({ item: page, score: best, type: 'page' });
        }
    }

    for (const action of getActions()) {
        const labelScore = fuzzyScore(q, action.label);
        const descScore = fuzzyScore(q, action.description) * 0.7;
        const keywordScore = Math.max(0, ...action.keywords.map(k => fuzzyScore(q, k))) * 0.8;
        const best = Math.max(labelScore, descScore, keywordScore);
        if (best > 0) {
            results.push({ item: action, score: best, type: 'action' });
        }
    }

    results.sort((a, b) => b.score - a.score);
    return results.slice(0, max);
}
